/**
 * useBottomSheet Hook
 *
 * Manages bottom sheet state, gestures, and animations.
 * Supports multiple snap points with smooth spring-like transitions.
 *
 * @param {Object} options
 * @param {Array} options.snapPoints - Array of snap points as percentages [0, 50, 100]
 * @param {number} options.initialSnap - Initial snap point index
 * @param {Function} options.onSnapChange - Callback when snap point changes
 */

import { useState, useCallback, useRef, useEffect } from 'react';

const VELOCITY_THRESHOLD = 0.5;
const DRAG_THRESHOLD = 50;

export const useBottomSheet = ({
  snapPoints = [0, 50, 90],
  initialSnap = 0,
  onSnapChange,
} = {}) => {
  const [currentSnap, setCurrentSnap] = useState(initialSnap);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const sheetRef = useRef(null);
  const dragStartY = useRef(0);
  const dragStartSnap = useRef(0);
  const lastY = useRef(0);
  const velocity = useRef(0);
  const animationFrame = useRef(null);

  const snapToPoint = useCallback((index) => {
    const clampedIndex = Math.max(0, Math.min(index, snapPoints.length - 1));
    setCurrentSnap(clampedIndex);
    setDragOffset(0);
    onSnapChange?.(clampedIndex);
  }, [snapPoints.length, onSnapChange]);

  const open = useCallback(() => {
    snapToPoint(snapPoints.length - 1);
  }, [snapToPoint, snapPoints.length]);

  const close = useCallback(() => {
    snapToPoint(0);
  }, [snapToPoint]);

  const toggle = useCallback(() => {
    if (currentSnap === 0) {
      snapToPoint(snapPoints.length - 1);
    } else {
      snapToPoint(0);
    }
  }, [currentSnap, snapToPoint, snapPoints.length]);

  const expandToHalf = useCallback(() => {
    if (snapPoints.length > 2) {
      snapToPoint(1);
    } else {
      open();
    }
  }, [snapPoints.length, snapToPoint, open]);

  const handleDragStart = useCallback((e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    dragStartSnap.current = currentSnap;
    lastY.current = clientY;
    velocity.current = 0;
    setIsDragging(true);

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  }, [currentSnap]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;

    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = clientY - dragStartY.current;

    // Calculate velocity
    velocity.current = clientY - lastY.current;
    lastY.current = clientY;

    // Convert to viewport percentage (inverted because sheet comes from bottom)
    const viewportHeight = window.innerHeight;
    const deltaPercent = (deltaY / viewportHeight) * 100;

    setDragOffset(-deltaPercent);
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const currentPercent = snapPoints[dragStartSnap.current] + dragOffset;

    // Determine direction based on velocity or position
    let targetIndex = dragStartSnap.current;

    if (Math.abs(velocity.current) > VELOCITY_THRESHOLD) {
      // Use velocity to determine direction
      if (velocity.current > 0) {
        // Dragging down, go to lower snap point
        targetIndex = Math.max(0, dragStartSnap.current - 1);
      } else {
        // Dragging up, go to higher snap point
        targetIndex = Math.min(snapPoints.length - 1, dragStartSnap.current + 1);
      }
    } else if (Math.abs(dragOffset) > DRAG_THRESHOLD / (window.innerHeight / 100)) {
      // Use position to determine closest snap point
      let closestIndex = 0;
      let closestDistance = Infinity;

      snapPoints.forEach((point, index) => {
        const distance = Math.abs(currentPercent - point);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      targetIndex = closestIndex;
    }

    snapToPoint(targetIndex);
  }, [isDragging, snapPoints, dragOffset, snapToPoint]);

  // Calculate current height percentage
  const currentHeight = snapPoints[currentSnap] + (isDragging ? dragOffset : 0);
  const isOpen = currentSnap > 0;
  const isFullyOpen = currentSnap === snapPoints.length - 1;

  // Touch event handlers for the drag handle
  const dragHandleProps = {
    onTouchStart: handleDragStart,
    onTouchMove: handleDragMove,
    onTouchEnd: handleDragEnd,
    onMouseDown: handleDragStart,
  };

  // Add mouse move/up listeners to window when dragging
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  return {
    sheetRef,
    currentSnap,
    currentHeight: Math.max(0, Math.min(100, currentHeight)),
    isDragging,
    isOpen,
    isFullyOpen,
    open,
    close,
    toggle,
    expandToHalf,
    snapToPoint,
    dragHandleProps,
  };
};

export default useBottomSheet;
